/* eslint-disable complexity */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { PatentProfileService } from '../patent-profile-service';
import type { PatentProfile as ServerPatentProfile } from '@/infrastructure/api-functions/core-drafting-api/patent-profile-by-ids-api';
import { parseHtml } from '@telecom/shared/utils/html';
import { CurrentTaskInputModel } from '@/domains/task/models/current-task-input-model';
import { PatentApi, type PatentProfileWithFamily } from '@telecom/shared/domain/patent';
import type { PatentProfile } from '../patent-profile-service';

@injectable()
export class PatentProfileServiceImpl implements PatentProfileService {
    constructor(
        @inject(PatentApi)
        private readonly _patentApi: PatentApi,
        @inject(CurrentTaskInputModel)
        private readonly _currentTaskInputModel: CurrentTaskInputModel,
    ) {
    }

    private _getLang() {
        return this._currentTaskInputModel.modelData.inputLanguage;
    }

    private getAbstLang(lang?: string, langs?: string[]): string {
        if (lang && langs?.includes(lang)) {
            return lang;
        }
        return langs?.[0] || lang || '';
    }

    public convertPatentProfile(patentProfile: Partial<ServerPatentProfile | PatentProfileWithFamily>, lang?: string): PatentProfile {
        const abstLang = this.getAbstLang(lang, Object.keys(patentProfile?.ABST || {}));
        const result: PatentProfile = {
            patentId: patentProfile?.PATENT_ID || '',
            apn: patentProfile.APN,
            title: parseHtml(patentProfile?.TITLE),
            patentNo: patentProfile?.PN || '',
            pbd: patentProfile?.PBD || '',
            ancs: patentProfile?.ANCS || [],
            apd:patentProfile?.APD || '',
            exdt:patentProfile?.EXDT || '',
            legalStatus:patentProfile?.LEGAL_STATUS || [],
            simpleLegalStatus:patentProfile?.SIMPLE_LEGAL_STATUS || 0,
            familyId:patentProfile?.FAMILY_ID || '',
            abst: typeof patentProfile?.ABST === 'string' ? patentProfile.ABST : patentProfile?.ABST?.[abstLang] || '',
            pdfImages: (patentProfile?.PDF_IMAGE || []) as any,
            in: patentProfile?.IN || [],
            simpleFamilyList: (patentProfile?.SIMPLE_FAMILY_LIST || []).map(item => ({
                patentId: item.PATENT_ID,
                pn: item.PN,
            })),
        };
        return result;
    }

    async getPatentProfile(patentId: string): Promise<PatentProfile> {
        const profileData = await this._patentApi.getPatentProfile(patentId, this._getLang());
        if (!profileData) {
            throw new Error(`Patent profile not found for ID: ${patentId}`);
        }
        // **重要** 这个接口没有返回PATENT_ID！！！
        if (!profileData.PATENT_ID) profileData.PATENT_ID = patentId;
        return this.convertPatentProfile(profileData, this._getLang());
    }

    async getPatentProfiles(patentIds: string[]): Promise<PatentProfile[]> {
        const profilesMap = await this._patentApi.getPatentProfiles(patentIds, this._getLang());
        return patentIds.map(id => {
            const profileData = profilesMap[id];
            if (!profileData) {
                console.warn(`Patent profile not found for ID: ${id}`);
            }
            // **重要** 这个接口没有返回PATENT_ID！！！
            if (!profileData.PATENT_ID) profileData.PATENT_ID = id;
            return this.convertPatentProfile(profileData, this._getLang());
        });
    }

    async getPatentProfileWithFamily(patentId: string): Promise<PatentProfile> {
        const profileData = await this._patentApi.getPatentProfileWithFamily(patentId, this._getLang());
        if (!profileData) {
            throw new Error(`Patent profile not found for ID: ${patentId}`);
        }
        // **重要** 这个接口没有返回PATENT_ID！！！
        if (!profileData.PATENT_ID) profileData.PATENT_ID = patentId;
        return this.convertPatentProfile(profileData, this._getLang());
    }

    async getPatentProfilesWithFamily(patentIds: string[]): Promise<PatentProfile[]> {
        const profilesMap = await this._patentApi.getPatentProfilesWithFamily(patentIds, this._getLang());
        return patentIds.map(id => {
            const profileData = profilesMap[id];
            if (!profileData) {
                console.warn(`Patent profile not found for ID: ${id}`);
            }
            // **重要** 这个接口没有返回PATENT_ID！！！
            if (!profileData.PATENT_ID) profileData.PATENT_ID = id;
            return this.convertPatentProfile(profileData, this._getLang());
        });
    }

    async getPatentAutocomplete(keyword: string): Promise<PatentProfile[]> {
        const { patent_info } = await this._patentApi.getPatentProfileByKeyword({ q: keyword, simple: true });

        if (!patent_info?.PATENT_ID) return [];

        const result = this.convertPatentProfile(patent_info, this._getLang());

        return [result];
    }

    async getPatentIdsByApnos(apnos: string[]): Promise<Record<string, string[]>> {
        return this._patentApi.getPatentIdsByApnos(apnos);
    }
}
